/**
 * SRD damage cantrips, as text.
 *
 * WHY THIS FILE EXISTS. `resolveSpellDamage` in core does the hard part — beam
 * counts, Agonizing Blast, cantrip die scaling — but it works by READING SPELL
 * PROSE, because that is the only form the game's own rules ship in. The only
 * source of that prose in this repo is `open5e-client.ts`, which is async: it
 * is an HTTP fetch behind a cache. The entire `GameSystemAdapter` surface is
 * synchronous, `listSpellAttacks` included, and for good reason — a caller
 * ranking a caster's options mid-turn cannot await a network round trip, and an
 * adapter that could stall is an adapter nobody routes combat through.
 *
 * So 5e keeps its own small sync table, exactly as it already does for SRD
 * weapons in `core/weapon-utils.ts`. The descriptions below are SRD text, kept
 * verbatim enough that `parseSpellDamage` and the cantrip-scaling detection in
 * `resolveSpellDamage` see the same sentences they would see coming back from
 * Open5e. That matters more than brevity: if the text is paraphrased, the
 * "when you reach 5th level" clause disappears and the spell silently stops
 * scaling.
 *
 * SCOPE. This is SRD-scoped, not complete. It covers the damage cantrips in the
 * SRD and nothing else — no Booming Blade, no Toll the Dead, nothing from a
 * supplement. A cantrip that is not in here is not an error, it is a spell this
 * table cannot answer for synchronously; the async Open5e path remains the
 * route for anything else, and `resolveSpellDamage` will handle its prose just
 * the same once a caller has awaited it.
 *
 * SAVE CANTRIPS ARE IN HERE TOO, marked `attackType: null`. Acid Splash and
 * Poison Spray deal damage but ask for a saving throw rather than an attack
 * roll, so `listSpellAttacks` skips them — a spell attack they are not. They
 * are still listed because they are damage cantrips, and a future
 * `listSpellSaves` should not have to rebuild the table to find them.
 */
export const SRD_CANTRIPS = {
    'eldritch blast': {
        name: 'Eldritch Blast',
        attackType: 'ranged',
        description: 'A beam of crackling energy streaks toward a creature within range. Make a ranged spell attack against the target. On a hit, the target takes 1d10 force damage. The spell creates more than one beam when you reach higher levels: two beams at 5th level, three beams at 11th level, and four beams at 17th level. You can direct the beams at the same target or at different ones. Make a separate attack roll for each beam.',
    },
    'fire bolt': {
        name: 'Fire Bolt',
        attackType: 'ranged',
        description: "You hurl a mote of fire at a creature or object within range. Make a ranged spell attack against the target. On a hit, the target takes 1d10 fire damage. A flammable object hit by this spell ignites if it isn't being worn or carried. This spell's damage increases by 1d10 when you reach 5th level (2d10), 11th level (3d10), and 17th level (4d10).",
    },
    'chill touch': {
        name: 'Chill Touch',
        attackType: 'ranged',
        description: "You create a ghostly, skeletal hand in the space of a creature within range. Make a ranged spell attack against the creature to assail it with the chill of the grave. On a hit, the target takes 1d8 necrotic damage, and it can't regain hit points until the start of your next turn. Until then, the hand clings to the target. This spell's damage increases by 1d8 when you reach 5th level (2d8), 11th level (3d8), and 17th level (4d8).",
    },
    'ray of frost': {
        name: 'Ray of Frost',
        attackType: 'ranged',
        description: "A frigid beam of blue-white light streaks toward a creature within range. Make a ranged spell attack against the target. On a hit, it takes 1d8 cold damage, and its speed is reduced by 10 feet until the start of your next turn. The spell's damage increases by 1d8 when you reach 5th level (2d8), 11th level (3d8), and 17th level (4d8).",
    },
    'shocking grasp': {
        name: 'Shocking Grasp',
        attackType: 'melee',
        description: "Lightning springs from your hand to deliver a shock to a creature you try to touch. Make a melee spell attack against the target. You have advantage on the attack roll if the target is wearing armor made of metal. On a hit, the target takes 1d8 lightning damage, and it can't take reactions until the start of its next turn. This spell's damage increases by 1d8 when you reach 5th level (2d8), 11th level (3d8), and 17th level (4d8).",
    },
    'thorn whip': {
        name: 'Thorn Whip',
        attackType: 'melee',
        description: "You create a long, vine-like whip covered in thorns that lashes out at your command toward a creature in range. Make a melee spell attack against the target. If the attack hits, the creature takes 1d6 piercing damage, and if the creature is Large or smaller, you pull the creature up to 10 feet closer to you. This spell's damage increases by 1d6 when you reach 5th level (2d6), 11th level (3d6), and 17th level (4d6).",
    },
    'produce flame': {
        name: 'Produce Flame',
        attackType: 'ranged',
        description: "A flickering flame appears in your hand. The flame remains there for the duration and harms neither you nor your equipment. You can also attack with the flame, although doing so ends the spell. When you cast this spell, or as an action on a later turn, you can hurl the flame at a creature within 30 feet of you. Make a ranged spell attack. On a hit, the target takes 1d8 fire damage. This spell's damage increases by 1d8 when you reach 5th level (2d8), 11th level (3d8), and 17th level (4d8).",
    },
    'poison spray': {
        name: 'Poison Spray',
        attackType: null,
        description: "You extend your hand toward a creature you can see within range and project a puff of noxious gas from your palm. The creature must succeed on a Constitution saving throw or take 1d12 poison damage. This spell's damage increases by 1d12 when you reach 5th level (2d12), 11th level (3d12), and 17th level (4d12).",
    },
    'acid splash': {
        name: 'Acid Splash',
        attackType: null,
        description: "You hurl a bubble of acid. Choose one creature you can see within range, or choose two creatures within 5 feet of each other. A target must succeed on a Dexterity saving throw or take 1d6 acid damage. This spell's damage increases by 1d6 when you reach 5th level (2d6), 11th level (3d6), and 17th level (4d6).",
    },
};
/** Look up a cantrip by name, case- and whitespace-insensitively. */
export function findCantrip(spellName) {
    return SRD_CANTRIPS[spellName.trim().toLowerCase()];
}
//# sourceMappingURL=cantrip-table.js.map